import { useContext, useEffect, useRef, useState } from 'react'
import { getWebSocketPool } from './WebSocketPool'

// export type TMessageDataHandler = (event: MessageEvent) => any;
export type TSendMessageHandler = (type: 'subscribe' | 'unsubscribe', params: any, socket: WebSocket) => any
export type TSendPingHandler = (socket: WebSocket) => any
export type TGetSocketMessageIdAndData = (event: MessageEvent) => Promise<any>
export type TGenerateMessageId = (params: any) => string

export type TConnects = {
  wsURL: string
  subscribeData: {
    [key: string]: {
      subscribeId: string
      subscribes: TSubscribe[]
    }
  }
  generateMessageId: TGenerateMessageId
  getSocketMessageIdAndData: TGetSocketMessageIdAndData
  // messageDataHandler: TMessageDataHandler;
  sendMessageHandler: TSendMessageHandler
  sendPingHandler: TSendPingHandler
  socket: WebSocket | null
}

type TOptions = {
  disableThrottling?: boolean
}

type TSubscribe = {
  subscribeId: string
  params: any
  callbackInfo: {
    callback: (data: any) => void
    options?: TOptions
  }
}
type TSubscribeData = TConnects['subscribeData']

type TCreateWebSocket = {
  wsURL: string
  subscribeData: TSubscribeData
  sendMessageHandler: TSendMessageHandler
  onReconnectSocket: (socket: WebSocket) => void
  sendPingHandler: (socket: WebSocket) => void
}

// 添加类型定义
type TListenerData = {
  [key: string]: Array<(event: any) => void>
}

const createWebSocket = ({
  wsURL,
  subscribeData,
  onReconnectSocket,
  sendMessageHandler,
  sendPingHandler,
}: TCreateWebSocket) => {
  // 使用 WebSocketPool 获取或创建 WebSocket 连接
  const pool = getWebSocketPool()
  let socket = pool.getWebSocket(wsURL)
  let pingPongTimer: NodeJS.Timeout
  let healthCheckTimer: NodeJS.Timeout
  let reconnectAttempts = 1
  const listenerData: TListenerData = {}

  const reconnectSocket = () => {
    // 无订阅数据时，关闭心跳检测
    if (!Object.keys(subscribeData).length) {
      clearTimeout(healthCheckTimer)
      return
    }

    // 无网络时，延迟重连
    // 应用在后台时，延迟重连
    if (!window.navigator?.onLine || window.document?.hidden) {
      setTimeout(reconnectSocket, Math.min(1000 * 2 ** reconnectAttempts, 50000))
      return
    }
    reconnectAttempts++
    if (socket?.readyState === WebSocket.CLOSED) {
      console.log('WebSocket.CLOSED')
      socket = createWebSocket({ wsURL, subscribeData, onReconnectSocket, sendMessageHandler, sendPingHandler })
      Object.entries(listenerData).forEach(([type, listeners]) => {
        listeners.forEach((listener) => {
          socket.addEventListener(type, listener)
        })
      })
      onReconnectSocket(socket)
    }
  }

  socket.addEventListener('open', () => {
    reconnectAttempts = 1
    // todo: 不确认该逻辑作用，后续再处理
    // if(dropTime) {
    //   let curTime = new Date().getTime()
    //   if((curTime - dropTime) >= 1000 * 60 * 4) {
    //     // 通知K线页面，用户ws断开连接后再次连接时获取历史K线数据
    //     store.commit('coinModuleCoinWebSocket', true)
    //   } else {
    //   }
    //   dropTime = undefined
    // }

    Object.keys(subscribeData).forEach((key) => {
      // socket?.send(JSON.stringify({
      //   params: subscribeData[key].subscribes[0]?.params,
      //   event: 'subscribe',
      // }));
      socket?.send(sendMessageHandler('subscribe', subscribeData[key].subscribes[0]?.params, socket))
    })

    pingPongTimer = setInterval(() => {
      // ping pong心跳，告知服务要保持连接。
      if (!window.navigator?.onLine) return
      sendPingHandler(socket)
    }, 10000)
  })

  socket.addEventListener('close', () => {
    clearInterval(pingPongTimer!)
  })

  socket.addEventListener('error', (err) => {
    clearInterval(pingPongTimer!)
  })

  socket.addEventListener('message', (event) => {
    clearTimeout(healthCheckTimer)
    healthCheckTimer = setTimeout(reconnectSocket, Math.min(1000 * 2 ** reconnectAttempts, 30000))
  })

  const socketAddEventListener = socket.addEventListener.bind(socket)
  const socketRemoveEventListener = socket.removeEventListener.bind(socket)

  // 重写addEventListener和removeEventListener，用于记录监听事件, 以便重连时重新监听
  socket.addEventListener = (type: keyof WebSocketEventMap, listener: TListener) => {
    if (listenerData[type]) {
      listenerData[type]!.push(listener)
    } else {
      listenerData[type] = [listener]
    }
    socketAddEventListener(type, listener)
  }
  socket.removeEventListener = (type: keyof WebSocketEventMap, listener: TListener) => {
    if (listenerData[type]) {
      listenerData[type]!.splice(listenerData[type]!.indexOf(listener), 1)
    }
    socketRemoveEventListener(type, listener)
  }
  return socket
}
type TProcessData<T extends keyof SocketTypeMap> = (data: SocketTypeMap[T]) => any

const createSocketMessageHandler = (context: TConnects) => {
  const timer: Record<string, number | undefined> = {}
  return (event: MessageEvent) => {
    // const messageId = context.generateMessageId(event)
    context.getSocketMessageIdAndData(event).then(({ key, data }) => {
      context.subscribeData[key]?.subscribes.forEach((subscribe) => {
        // 无需节流，直接执行
        if (!subscribe.callbackInfo.options?.disableThrottling) return
        subscribe.callbackInfo.callback(data)
      })
      if (timer[key]) {
        cancelAnimationFrame(timer[key]!)
      }
      timer[key] = requestAnimationFrame(() => {
        context.subscribeData[key]?.subscribes.forEach((subscribe) => {
          // 无需节流，之前已经执行过，此处不执行
          if (subscribe.callbackInfo.options?.disableThrottling) return
          subscribe.callbackInfo.callback(data)
        })
        timer[key] = undefined
      })
      // console.log('lastSocketHandler start', lastSocketHandler.current)
      // const value = marketCandlesMsg_pb.MarketCandlesPbMsg.deserializeBinary(res.data.data.value).toObject()
      // if (lastSocketHandler.current) {
      //   // console.log('lastSocketHandler cancel', lastSocketHandler.current)
      //   cancelAnimationFrame(lastSocketHandler.current)
      // }
      // lastSocketHandler.current = requestAnimationFrame(() => {
      //   // console.log('lastSocketHandler run', lastSocketHandler.current)
      //   callback(res.data)
      // })
      // console.log('lastSocketHandler init', lastSocketHandler.current)
    })
    // const res = context.messageHandler?.(event);
  }
}

const useAddSocketListener = (SocketContext: React.Context<TConnects>) => {
  const lastSocketHandler = useRef<number>()
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useAddSocketListener must be used within a SocketProvider')
  }

  const addSocketListener = (
    params: Record<string, any>,
    callback: TSubscribe['callbackInfo']['callback'],
    options?: TOptions
  ) => {
    const subscribeId = context.generateMessageId(params)

    callback(undefined) // 监听前默认先清空数据

    // todo: 前期debugger用后续删除
    // window.hc = window.hc ?? {}
    // window.hc.subscribeData = context.subscribeData

    const subscribeItem = {
      subscribeId,
      params,
      callbackInfo: {
        callback,
        options,
      },
    }
    // 新增订阅, 相同的key可能被多次订阅，之前没有订阅过，需要创建。如已经存在需要把新的订阅信息再加进去。
    if (!context.subscribeData[subscribeId]) {
      context.subscribeData[subscribeId] = {
        subscribeId,
        subscribes: [subscribeItem],
      }
    } else {
      context.subscribeData[subscribeId].subscribes.unshift(subscribeItem)
    }
    if (!context.socket) {
      console.log('!context.socket createWebSocket')
      context.socket = createWebSocket({
        wsURL: context.wsURL,
        subscribeData: context.subscribeData,
        sendMessageHandler: context.sendMessageHandler,
        onReconnectSocket: (socket) => {
          context.socket = socket
        },
        sendPingHandler: context.sendPingHandler,
      })
      context.socket.addEventListener('message', createSocketMessageHandler(context))
    } else if (context.socket.readyState === WebSocket.OPEN) {
      // context.socket.send(JSON.stringify({
      //   params,
      //   event: 'subscribe',
      // }));
      context.socket.send(context.sendMessageHandler('subscribe', params, context.socket))
    }

    const removeSocketListener = () => {
      // 取消订阅, 相同的key可能被订阅，之前有被记录下来，这边需要针对的进行删除。
      // 当这个key下没有订阅时，需要删除这个key，并且取消订阅。
      const subscribeInfo = context.subscribeData
      if (!subscribeInfo[subscribeId]) return
      subscribeInfo[subscribeId].subscribes = subscribeInfo[subscribeId]?.subscribes.filter(
        (subscribe) => subscribe !== subscribeItem
      )
      if (subscribeInfo[subscribeId].subscribes.length === 0) {
        delete subscribeInfo[subscribeId]
        if (context.socket?.readyState === WebSocket.OPEN) {
          context.socket?.send(context.sendMessageHandler('unsubscribe', params, context.socket))
        }
      }
      if (Object.keys(subscribeInfo).length === 0) {
        context.socket?.close()
        context.socket = null
      }
    }
    return removeSocketListener
  }
  return addSocketListener
}

const useSocketCallback = (SocketContext: React.Context<TConnects>, params: Record<string, any>, callback, options) => {
  const addSocketListener = useAddSocketListener(SocketContext)
  const callbackRef = useRef<TSubscribe['callbackInfo']>({ callback, options })
  callbackRef.current.callback = callback
  callbackRef.current.options = options

  // console.log('useSocketCallback params', callbackRef.current, params)
  useEffect(() => {
    return addSocketListener(params, callbackRef.current.callback, callbackRef.current.options)
  }, Object.values(params))
}

export const createUseSocket = <T>(SocketContext: React.Context<TConnects>) => {
  // 自定义 hook，便于使用 context
  function useSocket(
    params: { [k: string]: any },
    options?: TOptions | TProcessData<keyof SocketTypeMap>,
    processData?: TProcessData<keyof SocketTypeMap>
  ) {
    if (typeof options === 'function') {
      processData = options
      options = undefined
    }

    const lastSocketHandler = useRef<number>()
    const [subscribeData, setSubscribeData] = useState<SocketTypeMap[]>()
    const context = useContext(SocketContext)
    if (!context) {
      throw new Error('useSocket must be used within a SocketProvider')
    }

    const subscribeId = context.generateMessageId(params)

    useSocketCallback(
      SocketContext,
      params,
      (data) => {
        if (processData) {
          setSubscribeData(processData(data) || data)
        } else {
          setSubscribeData(data)
        }
      },
      options
    )

    return subscribeData
  }

  return useSocket
}

export const createUseSocketListener = (SocketContext: React.Context<TConnects>) => () =>
  useAddSocketListener(SocketContext)
